import React, { useState } from 'react';
import { Eye, X, CheckCircle, Ban, Search, ChevronLeft } from 'lucide-react';
import {  useNavigate } from 'react-router-dom';

const VitalAlertsTable = ({ alerts = [], onStatusUpdate = () => { } }) => {
  const navigate = useNavigate();

  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterVitalType, setFilterVitalType] = useState("");
  const [filterAlertType, setFilterAlertType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const openModal = (alert) => {
    setSelectedAlert(alert);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (selectedAlert?.alert_status === 'No Action') {
      onStatusUpdate(selectedAlert.alert_id, 'Viewed');
    }
    setIsModalOpen(false);
    setSelectedAlert(null);
  };

  const handleAction = (status) => {
    onStatusUpdate(selectedAlert.alert_id, status);
    setIsModalOpen(false);
    setSelectedAlert(null);
  };

  const formatTime = (dateString) => {
    const d = new Date(dateString);
    const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const day = d.getUTCDate();
    const year = d.getUTCFullYear();

    let hours = d.getUTCHours();
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`;
  };

  const getTypeColor = (type) => {
    return type === 'red' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'No Action': return 'bg-orange-100 text-orange-800';
      case 'Viewed': return 'bg-blue-100 text-blue-800';
      case 'Acknowledged': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredAlerts = alerts
    .filter(alert => {
      const matchesSearch = searchQuery === "" ||
        alert.vital_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.alert_note.toLowerCase().includes(searchQuery.toLowerCase()) ||
        alert.alert_type.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesVitalType = filterVitalType === "" || alert.vital_type.toLowerCase() === filterVitalType.toLowerCase();
      const matchesAlertType = filterAlertType === "" || alert.alert_type === filterAlertType;
      const matchesStatus = filterStatus === "" || alert.alert_status === filterStatus;

      return matchesSearch && matchesVitalType && matchesAlertType && matchesStatus;
    })
    .sort((a, b) => new Date(b.alert_time) - new Date(a.alert_time));

  return (
    <>
      <div className="bg-white h-screen">
        <div className="max-w-8xl mx-auto px-4 py-8">

          <div className="mb-8">
            <div className="flex flex-col gap-4 mb-6">

              <div className="bg-gray-100 p-4 rounded-lg flex-col">
                <div className="items-center space-x-4">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-10 flex items-center">
                      <button
                      onClick={() => navigate(-1)}
                        className="text-sm brand-border flex justify-center items-center py-2 text-brand-blue hover:brand-white hover:brand-blue transition-all duration-300 px-3 no-print"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        All Patients
                      </button>
                      <div className="ml-6 text-sm">
                        <h1 className="text-3xl font-bold text-gray-900 mb-1">Vital Alerts Dashboard</h1>
                        <p className="text-sm text-gray-600">
                          Monitor and manage critical patient vitals
                        </p>
                      </div>
                    </div>

                    <div className="col-span-2 flex items-center justify-end">
                      <div className="bg-white rounded-lg shadow-sm px-4 py-2 border border-gray-200">
                        <p className="text-xs text-gray-500">Total Alerts</p>
                        <p className="text-2xl font-bold text-gray-900 justify-center flex">{alerts.length}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-4 mt-4 lg:mt-0"></div>
              </div>
            </div>

            {/* Search and Filter Section */}
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              {/* Search Section */}
              <div className="relative w-full sm:w-96">
                <Search
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search alerts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
                />
              </div>

              {/* Filter Dropdowns */}
              <div className="flex gap-3 flex-wrap sm:flex-nowrap">
                {/* Vital Type Filter */}
                <select
                  value={filterVitalType}
                  onChange={(e) => setFilterVitalType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-sm font-medium text-gray-700 bg-white cursor-pointer"
                >
                  <option value="">All Vital Types</option>
                  <option value="spo2">SpO2</option>
                  <option value="hr">HR</option>
                  <option value="temperature">Temperature</option>
                </select>

                {/* Alert Type Filter */}
                <select
                  value={filterAlertType}
                  onChange={(e) => setFilterAlertType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-sm font-medium text-gray-700 bg-white cursor-pointer"
                >
                  <option value="">All Alert Types</option>
                  <option value="red">Critical</option>
                  <option value="amber">Warning</option>
                </select>

                {/* Status Filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150 text-sm font-medium text-gray-700 bg-white cursor-pointer"
                >
                  <option value="">All Status</option>
                  <option value="No Action">No Action</option>
                  <option value="Viewed">Viewed</option>
                  <option value="Acknowledged">Acknowledged</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {filteredAlerts.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <Search className="text-gray-400" size={28} />
              </div>
              <p className="text-gray-500 text-lg font-medium">No vital alerts found</p>
              <p className="text-gray-400 text-sm mt-1">All systems nominal</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto overflow-y-scroll">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Vital Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Vital Value
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Alert Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredAlerts.map((alert) => (
                      <tr key={alert.alert_id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200">
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                          <div className="font-semibold truncate">{alert.vital_type}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                          <div className="font-medium truncate">{alert.alert_note}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${getTypeColor(alert.alert_type)}`}>
                            {alert.alert_type === "red" ? "Critical" : "Warning"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap font-medium">
                          {formatTime(alert.alert_time)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${getStatusColor(alert.alert_status)}`}>
                            {alert.alert_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {alert.alert_status === 'No Action' || alert.alert_status === 'Viewed' ? (
                            <button
                              onClick={() => openModal(alert)}
                              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-600 hover:text-white transition-all duration-200 shadow-sm border border-blue-200"
                            >
                              <Eye size={16} />
                              Review
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs font-semibold">Resolved</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {isModalOpen && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-sm font-normal text-gray-600">{formatTime(selectedAlert.alert_time)}</p>
                <h3 className="text-2xl font-bold text-gray-900">Review Alert</h3>
                <p className="text-sm font-normal text-gray-500 mt-1">{selectedAlert.alert_note}</p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-all duration-150"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => handleAction('Cancelled')}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
              >
                <Ban size={18} />
                Cancel Alert
              </button>

              <button
                onClick={closeModal}
                className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-150"
              >
                Mark as Viewed
              </button>

              <button
                onClick={() => handleAction('Acknowledged')}
                className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
              >
                <CheckCircle size={18} />
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VitalAlertsTable;